import { useState } from 'react'
import { useNotify } from 'ra-core'
import { Button } from '@internal/ui-kit/components/ui/button'
import { MultiCombobox } from '@internal/ui-kit/components/ui/combobox'
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@internal/ui-kit/components/ui/drawer'
import { Input } from '@internal/ui-kit/components/ui/input'
import { Label } from '@internal/ui-kit/components/ui/label'
import { extractErrorMessage, httpClient } from '@internal/ui-kit/lib/httpClient'
import { useBrandOptions, useSlimProducts } from './useUserProfileData'

/**
 * `assignInventory` (`retool-exports/user-detail/queries.json`, `POST
 * users/inventories`, body `{...table1.newRows[0], owner_id}`) — ticket
 * feedback: deferred in the original PR, now requested. Field set matches
 * `table1`'s own editable/creatable columns exactly: `product_ids`
 * (`getProducts.data`, i.e. the real `slim-products` resource),
 * `brand_ids` (`getBrands.data`, the real `brands` resource), `quantity`,
 * and `tags` (the source's own `_columnOptionList.manualData: [{value:
 * "Maxicare"}]` plus `allowCustomValue: true` — a creatable multi-select,
 * not a fixed list).
 */
export function AssignInventoryDrawer({ userId, onCreated }: { userId: string; onCreated: () => void }) {
  const [open, setOpen] = useState(false)
  const [productIds, setProductIds] = useState<string[]>([])
  const [brandIds, setBrandIds] = useState<string[]>([])
  const [quantity, setQuantity] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const notify = useNotify()

  const { products } = useSlimProducts()
  const { brands } = useBrandOptions()

  const canSubmit = productIds.length > 0

  const reset = () => {
    setProductIds([])
    setBrandIds([])
    setQuantity('')
    setTags([])
  }

  const submit = async () => {
    setIsSubmitting(true)
    try {
      await httpClient('/users/inventories', {
        method: 'POST',
        body: JSON.stringify({
          owner_id: userId,
          product_ids: productIds,
          brand_ids: brandIds,
          quantity: quantity === '' ? undefined : Number(quantity),
          tags,
        }),
      })
      notify('Inventory assigned', { type: 'success' })
      reset()
      setOpen(false)
      onCreated()
    } catch (err) {
      notify(extractErrorMessage(err, 'Could not assign this inventory.'), { type: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Drawer open={open} onOpenChange={setOpen} direction="right">
      <Button type="button" size="sm" onClick={() => setOpen(true)}>
        Assign inventory
      </Button>
      <DrawerContent className="w-full sm:max-w-md">
        <DrawerHeader>
          <DrawerTitle>Assign inventory</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-4 overflow-y-auto p-4">
          <div className="flex flex-col gap-1.5">
            <Label>Products</Label>
            <MultiCombobox
              options={products.map((product) => ({ value: product.id, label: product.title ?? product.id }))}
              value={productIds}
              onChange={setProductIds}
              placeholder="Select products"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Brands</Label>
            <MultiCombobox
              options={brands.map((brand) => ({ value: brand.id, label: brand.name ?? brand.id }))}
              value={brandIds}
              onChange={setBrandIds}
              placeholder="Select brands"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="inventory-quantity">Quantity</Label>
            <Input id="inventory-quantity" type="number" value={quantity} onChange={(event) => setQuantity(event.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Tags</Label>
            <MultiCombobox
              options={[{ value: 'Maxicare', label: 'Maxicare' }]}
              value={tags}
              onChange={setTags}
              placeholder="Select or add tags"
              allowCustomValue
            />
          </div>
        </div>
        <DrawerFooter>
          <Button type="button" disabled={!canSubmit || isSubmitting} onClick={submit}>
            Assign inventory
          </Button>
          <DrawerClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
